import { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps, useHistory } from "react-router-dom";
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import InputAdornment from '@material-ui/core/InputAdornment';
import CircularProgress from '@material-ui/core/CircularProgress';
import Hidden from '@material-ui/core/Hidden';

import { TextField } from 'formik-material-ui';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { ReservationService } from '../../services';
import { ReservationResponse } from '../../services/models';
import {
  formatError,
  StatusId,
  snackbarUtils,
  handleSectionIcon,
  makeLuxonDate
} from '../../utils';

import Layout from '../../layout';
import Head from '../../components/Head';
import ScannerDialog from './ScannerDialog';
import { CheckInCard } from '../../assets/images'
import { QRCodeIcon } from '../../assets/icons';
import useCommonStyles, { blueButtonProps } from '../../styles/common';
import useStyles from './styles';
import { DateTime } from 'luxon';

interface ValueModel {
  code: string;
}

const Value: ValueModel = {
  code: ''
}

const ValidationSchema = Yup.object().shape({
  code: Yup.string().required('O código é obrigatório')
});

type TypeSchedule = {
  Data: string;
  Setor: string;
  Mesa: string;
  sectionName: string;
}

const mockScheduleData = (schedule: ReservationResponse): TypeSchedule => {
  return ({
    Data: makeLuxonDate(schedule.date),
    Setor: schedule.sectionName,
    Mesa: schedule.desk,
    sectionName: schedule.sectionName
  })
}

type MatchParams = {
  id: string;
}
interface Props extends RouteComponentProps<MatchParams> { }

const CheckIn = (props: Props) => {
  const history = useHistory();
  const commonClasses = useCommonStyles();

  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [schedule, setSchedule] = useState<TypeSchedule>({} as TypeSchedule);
  const classes = useStyles({ image: handleSectionIcon(schedule.sectionName) });
  const reservationId = Number(props.match.params.id);

  useEffect(() => {
    const IsToday = (reservation_date: string) =>
      DateTime.now().hasSame(DateTime.fromISO(reservation_date), "day");

    const fetchReservation = async () => {
      try {
        const { data } = await ReservationService.show(reservationId);
        if (data.statusId !== StatusId.checkIn) {
          snackbarUtils.warning('Não é possível realizar o check-in');
          history.replace(`/meu-agendamento/${data.id}`);
        } else if (!IsToday(data.date)) {
          snackbarUtils.warning(`O dia do seu agendamento é ${DateTime.fromISO(data.date).toLocaleString()}`);
          history.replace(`/meu-agendamento/${data.id}`);
        } else setSchedule(mockScheduleData(data));
      } catch (error) {
        console.log('Dados Agendamento: ', error.message);
        snackbarUtils.error(formatError(error));
      }
    };
    fetchReservation();
  }, [reservationId, history]);

  const handleDialog = useCallback(() => setDialogIsOpen(value => !value), []);

  const handleFormik = async (values: ValueModel, formikHelpers: FormikHelpers<ValueModel>) => {
    try {
      await ReservationService.checkIn(reservationId, values.code);
      history.push(`/meu-agendamento/${reservationId}`)
    } catch (error) {
      console.log('Check-in Erro: ', formatError(error));
      if (error.response.status === 400) snackbarUtils.warning('Código incorreto');
      else snackbarUtils.error(formatError(error));
    }
  }

  return (
    <Layout
      page="schedules"
      backLink="/agendamentos"
      loading={Object.keys(schedule).length === 0}
      innerPage
    >
      <Head>Dashboard</Head>
      <Box component="section" className={classes.container}>
        <Hidden only="xs">
          <Typography variant="h2">Check-in</Typography>
        </Hidden>

        <Hidden smUp>
          <Box className={classes.contentHeader}>
            <Grid container item xs={12} spacing={2}>
              {Object.entries(schedule).slice(0, 3).map(([key, value]) => (
                <Grid key={key} item xs>
                  <Typography variant="h3">{key}</Typography>
                  <Typography>{value}</Typography>
                </Grid>
              )
              )}
            </Grid>
          </Box>
        </Hidden>

        <Box className={classes.contentContainer}>
          <Box component="figure">
            <img src={CheckInCard} alt="check-in-card" draggable="false" />
          </Box>

          <Formik initialValues={Value} validationSchema={ValidationSchema} onSubmit={handleFormik}>
            {({ isSubmitting, values, touched, errors, setFieldValue }) => (
              <Form className={classes.form}>
                <Hidden mdUp>
                  <Button
                    onClick={handleDialog}
                    className={commonClasses.blueButton}
                    {...blueButtonProps}
                  >
                    Escanear Mesa
                  </Button>
                  <Box marginY={4}>
                    <Typography>Ou</Typography>
                  </Box>
                </Hidden>

                <Field
                  name="code"
                  component={TextField}
                  label="Informar Código"
                  error={touched.code && !!errors.code}
                  helperText={touched.code && errors.code}
                  InputProps={{
                    'className': commonClasses.inputs,
                    endAdornment: (
                      <InputAdornment position="end">
                        <QRCodeIcon />
                      </InputAdornment>
                    )
                  }}
                  size="small"
                />

                <Box mt={6}>
                  <Button
                    type="submit"
                    startIcon={isSubmitting ? <CircularProgress size="1rem" /> : null}
                    disabled={!values.code || isSubmitting}
                    className={commonClasses.blueButton}
                    {...blueButtonProps}
                  >
                    Confirmar Check-in
                </Button>
                </Box>
                <ScannerDialog
                  isOpen={dialogIsOpen}
                  handleClose={handleDialog}
                  setCode={(value: string) => setFieldValue('code', value)}
                />
              </Form>
            )}
          </Formik>
        </Box>
      </Box>
    </Layout>
  );
}

export default CheckIn;